import React from 'react'

import { ButtonStyles, cryptoTokens } from '@pijma/hero-types'
import { Spinner } from '@pijma/spinner'

export const ButtonStylesCryptoFactory: ButtonStyles<cryptoTokens> = (
  common,
) => ({
  styleProvider: {
    root: (props) => {
      const base = {
        transition: `all ${common.animation.s.animation}`,
        whiteSpace: 'nowrap' as const,
        cursor: props.loading ? 'default' : 'pointer',
      }

      const buttonGroupRelated = {
        '--padding': props.isButtonGroupStretch ? 'max(3.5cqw, 8px)' : '14px',
        '--corner-padding': props.isButtonGroupStretch
          ? 'calc(var(--padding) * 2 + 5px)'
          : '40px',

        '@supports (-webkit-hyphens:none)': {
          // safari only
          margin: '0 -0.6px', // necessary hack
        },

        ...(props.isButtonGroupStretch && {
          '@container button-group (width <= 432px)': {
            '--padding': 'max(2.5cqw, 8px)',
          },

          '@container button-group (width <= 384px)': {
            '--padding': 'max(2cqw, 8px)',
            ...common.text.caption['500'].typography,
          },

          '@supports not (contain: inline-size)': {
            '--padding': 'max(2.5%, 8px)',
            ...common.text.caption['500'].typography,
          },
        }),

        ...common.text.body[500].default.typography,
        borderRadius: 0,
        padding: '0 var(--padding) 2px',

        '&:first-of-type': {
          borderTopLeftRadius: common.border_radius.l.borderRadius,
          borderBottomLeftRadius: common.border_radius.l.borderRadius,
          paddingLeft: 'var(--corner-padding)',
          justifyContent: 'flex-end',
          flex: '1 1 auto',
        },

        '&:last-of-type': {
          borderTopRightRadius: common.border_radius.l.borderRadius,
          borderBottomRightRadius: common.border_radius.l.borderRadius,
          paddingRight: 'var(--corner-padding)',
          justifyContent: 'flex-start',
          flex: '1 1 auto',
        },
      }

      const primary = {
        ...base,

        width: props.width || 'auto',
        // display: 'flex',
        justifyContent: 'center',
        ...common.text.body[600].default.typography,
        borderRadius: common.border_radius.m.borderRadius,
        height:
          props.size === 'l'
            ? common.height.btn.l.sizing
            : common.height.btn.m.sizing,
        padding: `0 ${common.horizontal_padding.btn.general.m.sizing}`,
        backgroundColor: common.color.bg.btn.primary.default.color,
        color: common.color.font.dark_bg.primary.default.color,

        '&:hover, &:active': {
          backgroundColor: common.color.bg.btn.primary.hover.color,
        },

        '&[disabled]': {
          backgroundColor: common.color.bg.btn.primary.disabled.color,
          color: common.color.font.dark_bg.primary.disabled.color,
        },

        ...(props.isInButtonGroup ? buttonGroupRelated : {}),
      }

      const secondary = {
        ...primary,
        border: `1px solid ${common.border.btn.secondary.default.border.color}`,
        borderColor: common.border.btn.secondary.default.border.color,
        backgroundColor: common.color.bg.btn.secondary.default.color,
        color: common.color.font.light_bg.primary.default.color,

        '&:hover, &:active': {
          borderColor: common.border.btn.secondary.hover.border.color,
          backgroundColor: common.color.bg.btn.secondary.hover.color,
        },

        '&[disabled]': {
          borderColor: common.border.btn.secondary.disabled.border.color,
          backgroundColor: common.color.bg.btn.secondary.disabled.color,
          color: common.color.font.light_bg.primary.disabled.color,
        },
      }

      const text = {
        ...base,

        ...common.text.body[500].default.typography,
        borderRadius: common.border_radius.m.borderRadius,
        height: common.height.btn.text.sizing,
        padding: `0 ${common.horizontal_padding.btn.text.m.sizing}`,
        color: common.color.font.light_bg.primary.default.color,

        '&:hover, &:active': {
          backgroundColor: common.color.bg.btn.secondary.hover.color,
        },

        '&[disabled]': {
          color: common.color.font.light_bg.primary.disabled.color,
        },
      }

      const circle = {
        ...base,

        justifyContent: 'center',
        borderRadius: '100%',
        height: common.height.btn.m.sizing,
        width: common.height.btn.m.sizing,
        backgroundColor: common.color.bg.circle_btn.secondary.default.color,
        color: common.color.icon.light_bg.secondary.default.color,
        boxShadow: common.shadow.z1.boxShadow,

        '&:hover, &:active': {
          color: common.color.icon.light_bg.secondary.hover.color,
          boxShadow: common.shadow.z2.boxShadow,
        },

        '&[disabled]': {
          color: common.color.icon.light_bg.secondary.disabled.color,
        },
      }

      if (props.shape === 'circle') return circle
      if (props.type === 'primary') return primary
      if (props.type === 'text') return text

      return secondary
    },
    childrenWrapper: () => ({}),
    subtitle: (props) => ({
      ...common.text.body[500].default.typography,
      color: props.disabled
        ? common.color.font.dark_bg.secondary.disabled.color
        : common.color.font.dark_bg.secondary.default.color,
    }),
    startIcon: (props) => ({
      marginRight: props.shape === 'circle' ? 0 : '4px',
    }),
    endIcon: () => ({
      marginLeft: '8px',
    }),
    loadingWrapper: (props) => ({}),
  },
  components: {
    StartIcon: (props) => {
      const StartIcon = props.startIcon || props.icon
      if (!StartIcon) return null

      const size = common.size.icon.btn.sizing
      return <StartIcon width={size} height={size} />
    },
    EndIcon: (props) => {
      if (!props.endIcon) return null

      const size = common.size.icon.btn.sizing
      return <props.endIcon width={size} height={size} />
    },
    Spinner: () => {
      return null
    },
  },
})
